// Stok sayımı yazdırma: A4 kağıt sayım listesi ve sayım tutanağı.
// Yeni pencerede HTML açılır ve yazdırma penceresi gelir; "PDF olarak kaydet" ile PDF alınır.
import {
  fmtDate, fmtMoney, fmtQty, STATUS_LABEL, TYPE_LABEL,
  type StockCountDetail, type StockCountLine, type StockCountReview,
} from './stockCountService';

const esc = (v: unknown) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

const BASE_CSS = `
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; margin: 0; }
  h1 { font-size: 16px; margin: 0 0 4px; }
  .meta { margin: 0 0 10px; font-size: 11px; line-height: 1.6; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #999; padding: 5px 6px; text-align: left; vertical-align: top; }
  th { background: #eee; font-size: 10.5px; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  tr { page-break-inside: avoid; }
  thead { display: table-header-group; }
  .cat td { background: #f4f4f4; font-weight: bold; }
  .blank { min-width: 90px; }
  .foot { margin-top: 18px; line-height: 2.2; }
  .sig { display: flex; gap: 40px; margin-top: 24px; }
  .sig div { flex: 1; border-top: 1px solid #333; padding-top: 4px; text-align: center; }
  .pos { color: #1b5e20; } .neg { color: #b71c1c; }
  .kpis { display: flex; gap: 10px; margin: 8px 0 12px; }
  .kpis div { flex: 1; border: 1px solid #999; padding: 6px 8px; }
  .kpis b { display: block; font-size: 14px; }
  .muted { color: #555; }
  @media print { .noprint { display: none; } }
`;

function openPrintWindow(title: string, body: string) {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) {
    throw new Error('Yazdırma penceresi açılamadı. Tarayıcının açılır pencere engelini kapatın.');
  }
  w.document.open();
  w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${esc(title)}</title><style>${BASE_CSS}</style></head><body>${body}
<script>window.onload = function () { setTimeout(function () { window.focus(); window.print(); }, 150); };</script></body></html>`);
  w.document.close();
}

const scopeText = (count: StockCountDetail, categoryName: (id: string) => string | undefined) => {
  if (count.type === 'FULL') return 'Tam sayım';
  const cats = (count.scope?.categoryIds ?? []).map((id) => categoryName(id) || '').filter(Boolean);
  return cats.length ? `${TYPE_LABEL[count.type]} · ${cats.join(', ')}` : TYPE_LABEL[count.type];
};

/**
 * Kağıt sayım listesi: kategori sırasına göre, boş miktar kutularıyla.
 * Kör sayımda beklenen miktar basılmaz. Ek birimi olan ürüne "Koli / Adet" gibi iki kutu açılır.
 */
export function printCountSheet(
  count: StockCountDetail,
  opts: { categoryName: (id: string) => string | undefined; packUnits?: Record<string, string> },
) {
  const showExpected = count.showsExpected;
  const groups = new Map<string, StockCountLine[]>();
  for (const l of count.lines) {
    const key = (l.categoryId && opts.categoryName(l.categoryId)) || 'Kategorisiz';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(l);
  }
  const sorted = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'tr'));
  const colCount = showExpected ? 6 : 5;
  let n = 0;
  const rows = sorted.map(([cat, lines]) => {
    const items = lines
      .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '', 'tr'))
      .map((l) => {
        n += 1;
        const pack = opts.packUnits?.[l.itemId];
        const unit = pack ? `${esc(pack)} / ${esc(l.unit || 'Adet')}` : esc(l.unit || '');
        const blank = pack ? '____ / ____' : '________';
        return `<tr>
          <td class="num">${n}</td>
          <td>${esc(l.itemName)}${l.lotNo ? `<br><span class="muted">Lot ${esc(l.lotNo)}${l.expiryDate ? ` · SKT ${esc(new Date(l.expiryDate).toLocaleDateString('tr-TR'))}` : ''}</span>` : ''}</td>
          <td>${esc(l.barcode || l.stockCode || '')}</td>
          <td>${unit}</td>
          ${showExpected ? `<td class="num">${esc(fmtQty(l.snapshotQty))}</td>` : ''}
          <td class="blank">${blank}</td>
        </tr>`;
      })
      .join('');
    return `<tr class="cat"><td colspan="${colCount}">${esc(cat)}</td></tr>${items}`;
  }).join('');

  const body = `
    <h1>SAYIM LİSTESİ · ${esc(count.number)}${count.name ? ` · ${esc(count.name)}` : ''}</h1>
    <p class="meta">
      Depo: <b>${esc(count.warehouseName)}</b> · Kapsam: ${esc(scopeText(count, opts.categoryName))} · ${count.lines.length} ürün<br>
      Sayan: ______________________ · Tarih: ___ / ___ / ${new Date().getFullYear()} · Saat: ____:____
    </p>
    <table>
      <thead><tr><th class="num">#</th><th>Ürün</th><th>Barkod / Kod</th><th>Birim</th>${showExpected ? '<th class="num">Sistemde</th>' : ''}<th>Sayılan</th></tr></thead>
      <tbody>${rows || `<tr><td colspan="${colCount}">Sayım başlatılınca ürünler listelenir.</td></tr>`}</tbody>
    </table>
    <div class="foot">
      Listede olmayan ürünler: ____________________________________________________________________<br>
      ______________________________________________________________________________________________
    </div>
    <div class="sig"><div>Sayan imza</div><div>Kontrol eden imza</div></div>`;
  openPrintWindow(`Sayım listesi ${count.number}`, body);
}

/** Sayım tutanağı: özet, zaman çizelgesi, fark satırları, imza alanları. */
export function printCountReport(count: StockCountDetail, review: StockCountReview) {
  const diffLines = review.lines.filter((l) => (l.differenceQty ?? 0) !== 0);
  const surplusCount = diffLines.filter((l) => (l.differenceQty ?? 0) > 0).length;
  const shortageCount = diffLines.filter((l) => (l.differenceQty ?? 0) < 0).length;
  const counters = Array.from(new Set(review.lines.flatMap((l) => l.countedBy))).join(', ')
    || count.assignees.map((a) => a.userName || a.userUid).join(', ') || '—';
  const rows = diffLines
    .sort((a, b) => Math.abs(b.differenceValue ?? 0) - Math.abs(a.differenceValue ?? 0))
    .map((l, i) => {
      const d = l.differenceQty ?? 0;
      const cls = d > 0 ? 'pos' : 'neg';
      return `<tr>
        <td class="num">${i + 1}</td>
        <td>${esc(l.itemName)}${l.lotNo ? ` · ${esc(l.lotNo)}` : ''}${l.stockCode ? `<br><span class="muted">${esc(l.stockCode)}</span>` : ''}</td>
        <td class="num">${esc(fmtQty(l.expectedQty))} ${esc(l.unit || '')}</td>
        <td class="num">${l.countedQty == null ? 'sayılmadı' : esc(fmtQty(l.countedQty))}</td>
        <td class="num ${cls}">${d > 0 ? '+' : ''}${esc(fmtQty(d))}</td>
        <td class="num">${esc(fmtMoney(l.unitCost))}</td>
        <td class="num ${cls}">${esc(fmtMoney(l.differenceValue))}</td>
        <td>${esc(l.countedBy.join(', '))}${l.hasManualEntry ? ' <span class="muted">(elle)</span>' : ''}</td>
      </tr>`;
    }).join('');

  const body = `
    <h1>SAYIM TUTANAĞI · ${esc(count.number)}${count.name ? ` · ${esc(count.name)}` : ''}</h1>
    <p class="meta">
      Depo: <b>${esc(count.warehouseName)}</b> · Tür: ${esc(TYPE_LABEL[count.type])}${count.isBlind ? ' (kör sayım)' : ''} · Durum: ${esc(STATUS_LABEL[count.status])}<br>
      Oluşturuldu: ${esc(fmtDate(count.createdAt))}${count.createdByName ? ` (${esc(count.createdByName)})` : ''}
      · Başladı: ${esc(fmtDate(count.startedAt))}
      · Gönderildi: ${esc(fmtDate(count.submittedAt))}
      · Onaylandı: ${esc(fmtDate(count.approvedAt))}${count.approvedByName ? ` (${esc(count.approvedByName)})` : ''}<br>
      Sayanlar: ${esc(counters)}
      ${count.approvalNote ? `<br>Onay notu: ${esc(count.approvalNote)}` : ''}
    </p>
    <div class="kpis">
      <div>Sayılan satır<b>${review.countedLines} / ${review.lines.length}</b></div>
      <div>Sayım fazlası (${surplusCount} ürün)<b class="pos">+${esc(fmtMoney(review.surplusValue))}</b></div>
      <div>Sayım eksiği (${shortageCount} ürün)<b class="neg">${esc(fmtMoney(review.shortageValue))}</b></div>
      <div>Net fark<b class="${review.surplusValue + review.shortageValue < 0 ? 'neg' : 'pos'}">${esc(fmtMoney(review.surplusValue + review.shortageValue))}</b></div>
    </div>
    ${count.type === 'FULL' && review.uncountedLines > 0
      ? `<p class="meta">Sayılmayan ${review.uncountedLines} ürün: ${count.zeroedUncounted ? 'sıfırlandı (rafta yok sayıldı).' : 'olduğu gibi bırakıldı.'}</p>` : ''}
    ${review.movedDuringCountLines > 0
      ? `<p class="meta">${review.movedDuringCountLines} üründe sayım sırasında hareket oldu; beklenen miktar bu hareketlerle düzeltildi.</p>` : ''}
    <table>
      <thead><tr><th class="num">#</th><th>Ürün</th><th class="num">Beklenen</th><th class="num">Sayılan</th><th class="num">Fark</th><th class="num">Birim maliyet</th><th class="num">Tutar</th><th>Sayan</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="8">Fark yok; sayılan miktarlar sistemle aynı.</td></tr>'}</tbody>
    </table>
    ${review.unknownBarcodes.length
      ? `<p class="meta" style="margin-top:10px">Tanınmayan barkodlar (stoğa işlenmedi): ${review.unknownBarcodes.map((u) => `${esc(u.barcode)} × ${esc(fmtQty(u.quantity))}`).join(', ')}</p>` : ''}
    <p class="meta" style="margin-top:10px">Farklar stok hareketlerine "Sayım Fazlası" ve "Sayım Eksiği" olarak ${esc(count.number)} referansıyla işlenmiştir.</p>
    <div class="sig"><div>Sayan(lar)<br><b>${esc(counters)}</b></div><div>Kontrol eden</div><div>Onaylayan<br><b>${esc(count.approvedByName || '')}</b></div></div>`;
  openPrintWindow(`Sayım tutanağı ${count.number}`, body);
}
